/**
 * Railway Logger — forwards structured client-side events to the Railway
 * backend logs via POST /api/iap/log. Used to diagnose race conditions,
 * upgrade flows, and entitlement-refresh issues that don't surface in
 * device console.
 *
 * Usage:
 *   import { rlog } from '../utils/railwayLogger';
 *   rlog('dashboard_mount', { is_premium: true, plan: 'taraanaya_199', credits: 300 });
 *
 * All calls are fire-and-forget (non-blocking). Failures are silent.
 */

import API_URL from '../config/apiConfig';
import pkg from '../../package.json';

// Single source of truth: read version from package.json so we never have to bump
// this file separately. CRA / webpack inlines this at build time.
const APP_VERSION = pkg.version || 'unknown';

// Soft de-dupe: don't spam the same event+user within a short window.
// Keyed by `${event}|${user_id}` → last ms timestamp.
const LAST_SENT = new Map();
const DEDUPE_WINDOW_MS = 400;

// Limit payload size (Railway logs truncate at ~4kb)
const MAX_VALUE_LEN = 200;

const truncate = (v) => {
  if (v == null) return v;
  if (typeof v === 'string') return v.length > MAX_VALUE_LEN ? v.slice(0, MAX_VALUE_LEN) + '…' : v;
  if (Array.isArray(v)) return v.slice(0, 10);
  if (typeof v === 'object') {
    try {
      const s = JSON.stringify(v);
      return s.length > MAX_VALUE_LEN ? s.slice(0, MAX_VALUE_LEN) + '…' : v;
    } catch { return '[unserializable]'; }
  }
  return v;
};

const getCtx = () => {
  try {
    const ls = window.localStorage;
    return {
      user_id: ls.getItem('user_id') || ls.getItem('session_id') || 'anon',
      plan_id: ls.getItem('plan_id') || null,
      is_premium: ls.getItem('is_premium') === 'true',
      platform: (window.Capacitor?.getPlatform?.()) || 'web',
      app_version: APP_VERSION,
      ts: new Date().toISOString(),
    };
  } catch {
    return { user_id: 'anon', platform: 'web', ts: new Date().toISOString() };
  }
};

/**
 * Send a log event to Railway.
 * @param {string} event — short event name (e.g., 'dashboard_mount', 'upgrade_race')
 * @param {object} data — any extra fields (will be truncated for size)
 */
export const rlog = (event, data = {}) => {
  if (!event) return;
  try {
    const ctx = getCtx();
    const key = `${event}|${ctx.user_id}`;
    const now = Date.now();
    const last = LAST_SENT.get(key) || 0;
    if (now - last < DEDUPE_WINDOW_MS) return; // suppress duplicate within window
    LAST_SENT.set(key, now);

    const payload = { event, ...ctx };
    for (const [k, v] of Object.entries(data || {})) {
      if (k in payload) continue; // don't override context
      payload[k] = truncate(v);
    }

    // Fire-and-forget
    fetch(`${API_URL}/api/iap/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});

    // Also mirror to console for on-device debugging
    try {
      // eslint-disable-next-line no-console
      console.log(`[RLOG ${event}]`, payload);
    } catch {}
  } catch (e) {
    try { console.warn('[rlog] failed:', e?.message); } catch {}
  }
};

/** Log without de-duplication (use sparingly, for high-signal events). */
export const rlogForce = (event, data = {}) => {
  const key = `${event}|${getCtx().user_id}`;
  LAST_SENT.delete(key);
  rlog(event, data);
};

export default rlog;
